import axios from 'axios';
import { AdditionalBookingSectionEntity, TowOnTypeEntity } from 'Models/Entities';
import alertToast from 'Util/ToastifyUtils';

/**
 * Returns list of all tow on type entities with limited accessible properties.
 *
 * Includes the bare ids needed by the check-in/booking-wizard pickers PLUS the per-type weight
 * defaults (`minimumWeight.value`, `exactWeight`, `exactWeightUnit`) so the check-in exact-weight
 * input can pre-fill on trailer add. Without these the CheckInExactWeightInput prefill falls
 * through to null and the customer/staff has to type the value manually.
 *
 * Backed by the `TowOnTypeEntity/optimized` REST endpoint, which reads through the same
 * record-level security as the GraphQL query it replaced.
 */
export async function getTowOnTypesOptimized(): Promise<TowOnTypeEntity[]> {
	try {
		const response = await axios.get<TowOnTypeEntity[]>('/api/entity/TowOnTypeEntity/optimized');
		return (response.data ?? []).map(x => new TowOnTypeEntity(x));
	} catch (e) {
		console.error(e);
		alertToast('Something went wrong while fetching tow on types');
		return [];
	}
}

/**
 * Returns list of all additional booking section entities applicable to the given ferries and
 * routes, with limited accessible properties.
 *
 * BookingSections include id, name, information, bookingOptions.
 *
 * BookingOptions include id, name, isSingular, vehicleAddOn, disabled, removable.
 *
 * Backed by the `AdditionalBookingSectionEntity/optimized` REST endpoint, which reads through the
 * same record-level security as the GraphQL query it replaced.
 */
export async function getAdditionalBookingSections(config: {
	ferryIds?: string[];
	routeIds?: string[];
}): Promise<AdditionalBookingSectionEntity[]> {
	try {
		const params = new URLSearchParams();
		(config.ferryIds ?? []).forEach(id => params.append('ferryIds', id));
		(config.routeIds ?? []).forEach(id => params.append('routeIds', id));

		const queryStr = params.toString();
		const url = `/api/entity/AdditionalBookingSectionEntity/optimized${queryStr ? `?${queryStr}` : ''}`;

		const response = await axios.get<AdditionalBookingSectionEntity[]>(url);
		return (response.data ?? []).map(x => new AdditionalBookingSectionEntity(x));
	} catch (e) {
		console.error(e);
		alertToast('Something went wrong while fetching vehicle types');
		return [];
	}
}
